/**
 * Pet Care Essential — page grade-image config.
 * Image assets live in: public/biopet_essentialgrade/
 */

/** Folder under public/ (no leading or trailing slash). */
export const BIOPET_ESSENTIAL_GRADE_PUBLIC_DIR = 'biopet_essentialgrade';

/**
 * Maps evaluateBiopetEssentialRow() `label` strings to PNG filenames.
 */
export const BIOPET_ESSENTIAL_LABEL_TO_GRADE_IMAGE = {
  Deficient: 'deficient.png',
  'Slightly deficient': 'slightly_deficient.png',
  Normal: 'normal.png',
  Excessive: 'excessive.png',
  'Slightly excessive': 'slightly_excessive.png'
};

/**
 * @param {string} label - e.g. "Normal", "Deficient"
 * @returns {string} filename inside biopet_essentialgrade/
 */
export function biopetEssentialLabelToGradeFilename(label) {
  if (!label) return 'normal.png';
  return BIOPET_ESSENTIAL_LABEL_TO_GRADE_IMAGE[label] || 'normal.png';
}

/**
 * Page 5 layout: selected probiotics use grade images.
 * - group / speciesIndex must match BIOPET_ESSENTIAL_SPECIES_GROUPS + evaluateBiopetEssentialRow()
 * - grade positions are in px, relative to the overlay wrapper.
 */
export const BIOPET_ESSENTIAL_PAGE5_GRADE = {
  page: 5,
  gradeBasePath: BIOPET_ESSENTIAL_GRADE_PUBLIC_DIR,
  overlay: {
    top: 525,
    left: 120,
    width: 980
  },
  species: [
    {
      speciesName: 'Bifidobacterium infantis',
      group: 'probiotics',
      speciesIndex: 0,
      grade: { x: 223, y: -100, width: 140, height: 31 }
    },
    {
      speciesName: 'Enterococcus faecium',
      group: 'probiotics',
      speciesIndex: 1,
      grade: { x: 223, y: 106, width: 140, height: 31 }
    },
    {
      speciesName: 'Lactobacillus acidophilus',
      group: 'probiotics',
      speciesIndex: 2,
      grade: { x: 223, y: 313, width: 140, height: 31 }
    }
  ]
};

/**
 * Page 6 layout: selected probiotics use grade images.
 */
export const BIOPET_ESSENTIAL_PAGE6_GRADE = {
  page: 6,
  gradeBasePath: BIOPET_ESSENTIAL_GRADE_PUBLIC_DIR,
  overlay: {
    top: 525,
    left: 120,
    width: 980
  },
  species: [
    {
      speciesName: 'Lactobacillus paracasei',
      group: 'probiotics',
      speciesIndex: 3,
      grade: { x: 223, y: -342, width: 140, height: 31 }
    },
    {
      speciesName: 'Lactobacillus plantarum',
      group: 'probiotics',
      speciesIndex: 4,
      grade: { x: 223, y: -170, width: 140, height: 31 }
    },
    {
      speciesName: 'Lactobacillus rhamnosus',
      group: 'probiotics',
      speciesIndex: 5,
      grade: { x: 223, y: 1, width: 140, height: 31 }
    },
    {
      speciesName: 'Streptococcus thermophilus',
      group: 'probiotics',
      speciesIndex: 6,
      grade: { x: 223, y: 156, width: 140, height: 31 }
    }
  ]
};

/**
 * Page 7 layout: core + harmful bacteria use grade images.
 */
export const BIOPET_ESSENTIAL_PAGE7_GRADE = {
  page: 7,
  gradeBasePath: BIOPET_ESSENTIAL_GRADE_PUBLIC_DIR,
  overlay: {
    top: 525,
    left: 120,
    width: 980
  },
  species: [
    {
      speciesName: 'Faecalibacterium',
      group: 'core',
      speciesIndex: 0,
      grade: { x: 223, y: -342, width: 140, height: 31 }
    },
    {
      speciesName: 'Peptacetobacter hiranomis',
      group: 'core',
      speciesIndex: 1,
      grade: { x: 223, y: -170, width: 140, height: 31 }
    },
    {
      speciesName: 'Escherichia coli',
      group: 'harmful',
      speciesIndex: 0,
      grade: { x: 223, y: 1, width: 140, height: 31 }
    },
    {
      speciesName: 'Streptococcus',
      group: 'harmful',
      speciesIndex: 1,
      grade: { x: 223, y: 192, width: 140, height: 31 }
    }
  ]
};

/**
 * Build segment names for the page-8 supplement bar (cycles through recommendations).
 * 1 supplement → same name × segmentCount; 2 → A,B,A,B,...; 3 → A,B,C,A,B,C.
 */
export function buildBiopetPage8SupplementBarSegments(supplements, segmentCount = 6) {
  const list = (supplements || []).filter(Boolean);
  if (!list.length) return [];
  return Array.from({ length: segmentCount }, (_, i) => list[i % list.length]);
}

/**
 * Page 8 layout: recommended supplement image slots.
 */
export const BIOPET_ESSENTIAL_PAGE8_SUPPLEMENT = {
  page: 8,
  supplementBasePath: 'supp_biopet_professional',
  // Slot coordinates/sizes are designed for this width.
  // Increasing overlay.width will now scale image sizes/positions automatically.
  baseWidth: 980,
  imageBySupplement: {
    PetGard: 'petguard.png',
    PetReno: 'petreno.png',
    PetFlow: 'petflow.png'
  },
  // Default overlay (used for 2/3 supplements unless overridden).
  overlay: {
    top: 520,
    left: 120,
    width: 980
    // height: 600
  },
  // Per-count overlay overrides.
  // Here, 1 supplement uses your requested position.
  overlayByCount: {
    1: {
      top: 400,
      left: 0,
      width: 2000,
      height: 500
    }
  },
  // Per-count image slot layout:
  // - 2 supplements: first on left, second on right
  // - 3 supplements: line up in one row
  slotByCount: {
    2: [
      { x: -80, y: -350, width: 440, height: 440, height: 350 }, // first (left)
      { x: 200, y: -350, width: 440, height: 440, height: 350 } // second (right)
    ],
    3: [
      { x: 80, y: -140, width: 240, height: 240 }, // first
      { x: 380, y: -140, width: 240, height: 240 }, // second
      { x: 680, y: -140, width: 240, height: 240 } // third
    ]
  },
  slots: [
    { x: 80, y: -140, width: 240, height: 240 },
    { x: 380, y: -140, width: 240, height: 240 },
    { x: 680, y: -140, width: 240, height: 240 }
  ],
  /**
   * Separate layer from supplement images — same top/left/width for 1, 2, or 3 supplements
   * (page-8 PNG coordinates). Tune this so the sentence stays in one stable place.
   */
  recommendationOverlay: {
    top: 520,
    left: 125,
    width: 980,
    zIndex: 5
  },
  /**
   * Horizontal “bar” of boxes (default 6) showing supplement names, alternating when multiple.
   * Position is page-8 absolute, independent of the image overlay (same idea as recommendationOverlay).
   */
  supplementBar: {
    enabled: true,
    overlay: {
      top: 779,
      left: 210,
      width: 980,
      zIndex: 4
    },
    /** Offset inside supplementBar.overlay */
    x: 0,
    y: 0,
    /** Total width of the row of boxes (px). If omitted, uses overlay.width minus x padding. */
    barWidth: 449,
    segmentHeight: 22,
    segmentCount: 6,
    gap: 3,
    borderRadius: 0,
    fontSize: 14,
    fontWeight: 700,
    fontFamily: "'Gilroy', system-ui, sans-serif",
    /** Per supplement: backgroundColor, optional color (text), optional displayLabel */
    supplementStyles: {
      PetFlow: {
        backgroundColor: '#1d9391',
        color: '#ffffff'
      },
      PetGard: {
        backgroundColor: '#e0577f',
        color: '#ffffff'
      },
      PetReno: {
        backgroundColor: '#e4734c',
        color: '#ffffff'
      }
    },
    defaultStyle: {
      backgroundColor: '#64748b',
      color: '#ffffff'
    }
  },
  /**
   * scaleSlotsWithOverlay: image slot coords vs overlay.width.
   * scaleRecommendationTextWithOverlay: opt-in to scale recommendationText x/y/width with recommendationOverlay.width.
   */
  scaleSlotsWithOverlay: true,
  scaleRecommendationTextWithOverlay: false,
  recommendationText: {
    enabled: true,
    prefix: 'According to the analysis result, it is recommended to use ',
    /**
     * Text lives in recommendationOverlay only. anchor/belowImages is not used (use fixed x, y here).
     */
    anchor: 'fixed',
    marginTop: 12,
    yFineTune: 0,
    x: -218,
    y: 83,
    width: 980,
    /** When false (default), fontSize is always this many px regardless of overlay width / supplement count. */
    scaleFontWithOverlay: false,
    fontSize: 15,
    color: '#044a5b',
    fontFamily: "'Gilroy', system-ui, sans-serif",
    fontWeight: 800,
    lineHeight: 1.25,
    textAlign: 'center'
  }
};
